<template>
  <!--
    O PAINEL DO IA4COM NO RELATORIO DE CHAMADAS (TEL-2327).

    O botao da linha abre o assistente AO LADO da lista, sem navegar: periodo,
    filtros e paginas carregadas continuam onde estavam. Clicar em outra ligacao
    troca o contexto do mesmo painel, sem recarregar o quadro.

    FECHAR ESCONDE, NAO DESTROI: reabrir e instantaneo e a conversa continua.
    O quadro so nasce no primeiro clique e morre quando a pessoa sai do relatorio
    (este componente vive dentro de Report/Calls.vue).
  -->
  <aside
    v-show="aberto"
    class="painel-ligacao"
    role="complementary"
    aria-label="Assistente IA4COM">
    <header class="painel-ligacao__cabecalho">
      <div class="painel-ligacao__marca" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 2.5 11.75 7.75 17 9.5 11.75 11.25 10 16.5 8.25 11.25 3 9.5 8.25 7.75Z" />
          <path d="M17.5 13 18.4 15.6 21 16.5 18.4 17.4 17.5 20 16.6 17.4 14 16.5 16.6 15.6Z" />
        </svg>
      </div>
      <div class="painel-ligacao__titulo">
        <strong>IA4COM <span class="painel-ligacao__beta">Beta</span></strong>
        <span v-if="descricao" class="painel-ligacao__descricao" :title="descricao">{{ descricao }}</span>
      </div>
      <button
        type="button"
        class="painel-ligacao__acao"
        title="Abrir o assistente em tela cheia"
        aria-label="Abrir o assistente em tela cheia"
        @click="expandir">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </svg>
      </button>
      <button
        type="button"
        class="painel-ligacao__acao"
        title="Fechar (Esc)"
        aria-label="Fechar o assistente"
        @click="$emit('fechar')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </header>

    <QuadroIA4COM
      v-if="montado"
      class="painel-ligacao__quadro"
      painel
      :visivel="aberto"
      :call-id="ligacao && ligacao.callId"
      :pedido-em="pedidoEm" />
  </aside>
</template>

<script>
import QuadroIA4COM from './QuadroIA4COM.vue'

/** a largura do painel; o relatorio reserva o mesmo espaco a direita enquanto ele esta aberto */
const LARGURA_DO_PAINEL_PX = 440

/**
 * Abaixo disto a lista nao cabe ao lado do painel: ele cobre, como um modal. (A
 * barra lateral do portal so some em 1024px; entre 1024 e 1200 ela ainda ocupa
 * 255px, e com os 440 do painel nao sobraria lista para ler.)
 */
const LARGURA_MINIMA_PARA_LADO_A_LADO_PX = 1200

export default {
  name: 'PainelLigacao',

  components: { QuadroIA4COM },

  props: {
    aberto: { type: Boolean, default: false },
    /** { callId, inicio, vendedor } da ligacao escolhida */
    ligacao: { type: Object, default: null },
    /** muda a cada troca de ligação — ver `pedidoEm` em QuadroIA4COM.vue */
    pedidoEm: { type: Number, default: 0 }
  },

  data () {
    return {
      /** o quadro so nasce no primeiro clique: o relatorio sem clique nao carrega o IA4COM */
      montado: false,
      /** o .main-content do portal e o `padding-right` que ele tinha antes do painel */
      areaDoConteudo: null,
      paddingAnterior: ''
    }
  },

  computed: {
    descricao () {
      if (!this.ligacao) return ''
      return [this.ligacao.inicio, this.ligacao.vendedor].filter(Boolean).join(' · ')
    }
  },

  watch: {
    aberto: {
      immediate: true,
      handler (aberto) {
        if (aberto) this.montado = true
        this.$nextTick(() => this.reservarEspaco(aberto))
      }
    }
  },

  mounted () {
    window.addEventListener('keydown', this.aoTeclar)
    window.addEventListener('resize', this.aoRedimensionar)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.aoTeclar)
    window.removeEventListener('resize', this.aoRedimensionar)
    this.reservarEspaco(false)
  },

  methods: {
    /**
     * A largura mudou com o painel aberto (janela arrastada, DevTools, tablet
     * girado): devolve o espaco e decide de novo. Sem isto, o que foi decidido na
     * abertura valia ate fechar — lista espremida ao encolher, colunas cobertas ao
     * crescer.
     */
    aoRedimensionar () {
      if (!this.aberto) return
      this.reservarEspaco(false)
      this.reservarEspaco(true)
    },

    aoTeclar (event) {
      if (this.aberto && (event.key === 'Escape' || event.key === 'Esc')) this.$emit('fechar')
    },

    /**
     * Sem isto o painel cobre as colunas da direita (os botoes da linha) e a
     * paginacao. O espaco e reservado no PROPRIO elemento, e o valor de antes e
     * devolvido ao fechar — o mesmo cuidado de `soltarAlturaMinima` em
     * Index.vue: uma regra global de CSS valeria para o portal inteiro.
     */
    reservarEspaco (reservar) {
      /*
       * DEVOLVER NAO PROCURA NADA NO DOM. No `beforeDestroy` o Vue ja tirou o
       * painel da pagina: `$el.closest` volta nulo, e procurar a area antes de
       * devolver deixava o `padding-right` preso no .main-content. Sair do
       * relatorio com o painel aberto (o Expandir) levava a sobra para o portal
       * inteiro — o /assistant estreito, e o relatorio com a lateral vazia na volta.
       * A area guardada na reserva e a que se devolve.
       */
      if (!reservar) {
        if (!this.areaDoConteudo) return
        this.areaDoConteudo.style.paddingRight = this.paddingAnterior
        this.areaDoConteudo = null
        return
      }
      // em tela estreita nao ha lista que caiba ao lado: o painel cobre, como um modal
      if (this.areaDoConteudo || window.innerWidth < LARGURA_MINIMA_PARA_LADO_A_LADO_PX) return
      const area = this.$el && this.$el.closest && this.$el.closest('.main-content')
      if (!area) return
      this.areaDoConteudo = area
      this.paddingAnterior = area.style.paddingRight
      area.style.paddingRight = `${LARGURA_DO_PAINEL_PX + 15}px`
    },

    /**
     * Tela cheia, SEM a ligacao na URL: a pagina abre a conversa mais recente, que
     * e a do painel quando ja houve pergunta. Mandar o `callId` criaria uma
     * segunda conversa para a mesma ligacao.
     */
    expandir () {
      this.$router.push({ name: 'ia4com' })
    }
  }
}
</script>

<style scoped>
/*
 * Abaixo do cabecalho fixo do portal (60px + a faixa de aviso, quando ha) e da
 * barra lateral e dos modais do Bootstrap: z-index 1020 fica sob o header (1030),
 * a sidebar (1040) e os modais (1050) — o modal de metadados do relatorio abre
 * por cima do painel, como deve.
 */
.painel-ligacao {
  position: fixed;
  top: calc(60px + var(--banner-height, 0px));
  right: 0;
  bottom: 0;
  width: 440px;
  max-width: 100vw;
  z-index: 1020;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -8px 0 24px rgba(17, 24, 39, 0.08);
}

.painel-ligacao__cabecalho {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

/* o ciano da faisca, o mesmo do item no menu e da pagina /assistant */
.painel-ligacao__marca {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 0.5rem;
  background: #36bbef;
  color: #ffffff;
  flex-shrink: 0;
}
.painel-ligacao__marca svg {
  width: 18px;
  height: 18px;
}

.painel-ligacao__titulo {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  line-height: 1.25;
  color: #1f2937;
  font-size: 0.875rem;
}

.painel-ligacao__descricao {
  color: #6b7280;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* mesmo desenho do `.ia4com-beta` da pagina /assistant */
.painel-ligacao__beta {
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #007bff;
  color: #fff;
  vertical-align: middle;
  margin-left: 4px;
}

.painel-ligacao__acao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
}
.painel-ligacao__acao:hover,
.painel-ligacao__acao:focus {
  background: #f3f4f6;
  color: #111827;
}
.painel-ligacao__acao svg {
  width: 16px;
  height: 16px;
}

.painel-ligacao__quadro {
  flex: 1;
  min-height: 0;
}

/* no painel o card nao precisa de raio nem sombra: o painel ja e a moldura */
.painel-ligacao__quadro >>> .ia4com-card {
  border-radius: 0;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// src/components/IA4COM/PainelLigacao.vue